import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import { faTrashAlt } from "@fortawesome/pro-solid-svg-icons";
import API from "Signups/API";
import { getSignupErrorMessage } from "Signups/SharedComponents/getSignupErrorMessage";
import { signupItemStateHooks } from "Signups/SignupView/SignupSheet/Screens/SignupItems/stateHooks.js";
import { useGetSignupListType } from "Signups/SignupView/utils/useGetSignupListType";
import { useSignups } from "Signups/state";
import { motion } from "framer-motion";

import FnrButton from "UI/FnrButton3";
import FnrConfirmationPopover from "UI/FnrConfirmationPopover";

import useHovering from "Utilities/Hooks/useHovering";

import DeletePaidSignupModal from "../../DeletePaidSignupModal";
import { personType } from "../../DeletePaidSignupModal/personType";

import AddRespondent from "./AddRespondent";
import AdditionalName from "./AdditionalName";
import ExtraSlotsWithoutNames from "./ExtraSlotsWithoutNames";
import HouseholdMember from "./HouseholdMember";
import HoverButton from "./HoverButton";

Household.propTypes = {
  household: PropTypes.object,
  highlight: PropTypes.bool,
};

export default function Household({
  household,
  highlight,
  signupItemID,
  hasSignupCost,
  removalType,
  setRemovalType,
}) {
  const { householdID, householdTitle, householdRespondents, numberOfSlots } =
    household;
  const [state, dispatch] = useSignups();
  const [isHovering, hoverProps] = useHovering();
  const [status, setStatus] = useState(null);
  // const [showConfirmation, setShowConfirmation] = useState(false);
  const confirmationRef = useRef();
  const { isMemberType, isItemType } = useGetSignupListType();
  const { getSignupItem } = signupItemStateHooks.useGetSignupItem();

  const [showDeletePopover, setShowDeletePopover] = useState(false);
  const [showAddPopover, setShowAddPopover] = useState(null); // null, "household", "additionalName"
  const [hasActiveHoverItem, setHasActiveHoverItem] = useState(null);
  const [showDeletePaidSignupModal, setShowDeletePaidSignupModal] =
    useState(false);

  const paidSlotsCount =
    hasSignupCost &&
    householdRespondents?.filter(
      (respondent) =>
        respondent?.paymentStatus === "Paid" ||
        respondent?.paymentStatus === "MarkedAsPaid"
    ).length;
  const unpaidSlotsCount =
    hasSignupCost &&
    householdRespondents?.filter(
      (respondent) => respondent?.paymentStatus === "Unpaid"
    ).length;

  // console.log("householdRespondents", householdRespondents);

  // This is used to keep the hover buttons visible if any items popover is currently open.
  useEffect(() => {
    if (showDeletePopover || showAddPopover) {
      setHasActiveHoverItem(true);
    } else {
      setHasActiveHoverItem(false);
    }
  }, [showDeletePopover, showAddPopover]);

  const handleTrashCanClick = () => {
    const someRespondentsArePaid = householdRespondents.some(
      (householdMember) => householdMember.paymentStatus === "Paid"
    );

    if (someRespondentsArePaid) {
      setRemovalType(personType.HOUSEHOLD);
      setShowDeletePaidSignupModal(true);
      return;
    }

    toggleDeletePopover();
  };

  const toggleDeletePopover = () =>
    setShowDeletePopover((showDeletePopover) => !showDeletePopover);

  const getUserIDForAddingAdditionalNames = () => {
    // when adding an additional name, you need to pass in a UserID that the name will be added under
    let userID = householdRespondents.filter((u) => u.userID)[0]?.userID;
    return userID;
  };

  const handleDeleteClick = () => {
    setStatus("loading");

    API.cancelAllHouseholdUserSignups({
      signupListID: state.currentSignupList.ID,
      signupItemID,
      householdID,
      signedUpViaID: 5,
    })
      .post()
      .then((resp) => {
        setStatus(null);
        getSignupItem(signupItemID);
        dispatch({
          type: "Other User Signup Updated",
          signupList: resp.signupList,
          allSignupLists: resp.allSignupLists,
        });
        toggleDeletePopover();
      })
      .catch((err) => {
        alert(getSignupErrorMessage(err?.error));
        setStatus(null);
        setShowDeletePopover(false);
      });
  };

  return (
    <Wrapper highlight={highlight}>
      <TitleRow {...hoverProps}>
        <Title>{householdTitle}</Title>
        <TitleEnd>
          <span ref={confirmationRef}>
            <HoverButton
              icon={faTrashAlt}
              isHovering={isHovering || hasActiveHoverItem}
              isActive={showDeletePopover}
              onClick={handleTrashCanClick}
            />
          </span>
          <AddRespondent
            isHovering={isHovering || hasActiveHoverItem}
            showAddPopover={showAddPopover}
            setShowAddPopover={setShowAddPopover}
            signupItemID={signupItemID}
            householdID={householdID}
            userID={getUserIDForAddingAdditionalNames()}
          />
          <NumberOfSlots>{numberOfSlots}</NumberOfSlots>
        </TitleEnd>
      </TitleRow>

      {householdRespondents.map((user, i) => {
        // only show the first actual respondent for item type (the rest are empty additional names that represent slots, but don't need to be shown)
        if (isItemType && user.isAdditionalName) return null;

        if (user.userID) {
          return (
            <HouseholdMember
              key={user.userID + i}
              user={user}
              signupItemID={signupItemID}
              hasSignupCost={hasSignupCost}
              setRemovalType={setRemovalType}
              removalType={removalType}
              unpaidSlotsCount={unpaidSlotsCount}
              paidSlotsCount={paidSlotsCount}
            />
          );
        } else {
          return (
            <AdditionalName
              key={user.additionalNameID + 999}
              user={user}
              hasSignupCost={hasSignupCost}
              setRemovalType={setRemovalType}
              removalType={removalType}
              signupItemID={signupItemID}
            />
          );
        }
      })}

      {isMemberType ? (
        <ExtraSlotsWithoutNames
          users={householdRespondents}
          signupItemID={signupItemID}
          setRemovalType={setRemovalType}
        />
      ) : null}

      <FnrConfirmationPopover
        show={showDeletePopover}
        ref={confirmationRef}
        onClose={() => setShowDeletePopover(false)}
        message={`Are you sure you want to cancel the Signup for ${householdTitle}?`}
        button1={
          <FnrButton
            text="Yes, cancel it!"
            onClick={handleDeleteClick}
            color="red"
            loading={status === "loading"}
          />
        }
        color="red"
        style={{ maxWidth: 200, padding: 10 }}
      />

      <DeletePaidSignupModal
        household={household}
        signupItemID={signupItemID}
        showDeletePaidSignupModal={showDeletePaidSignupModal}
        setShowDeletePaidSignupModal={setShowDeletePaidSignupModal}
        removalType={removalType}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  margin-bottom: 8px;
  background-color: ${({ highlight }) => (highlight ? "#eceff0" : null)};
  padding: 4px 8px;
  border-radius: 8px;
`;

const TitleRow = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  font-size: 14px;
`;

const Title = styled.div`
  color: #333;
  font-weight: 400;
`;

const TitleEnd = styled.div`
  display: flex;
  align-items: center;
`;

const NumberOfSlots = styled.div`
  margin-left: 3px;
  color: #333;
  font-weight: 400;
`;
